/**
 * @file AssignmentTable.jsx
 * @description AssignmentTable component for displaying and managing assignment table.
 * @component AssignmentTable
 * @exports AssignmentTable - Main component for displaying and managing assignment table.
 */

import { useState } from "react";
import Menu from "@mui/material/Menu";
import Table from "@mui/material/Table";
import { Edit } from "@mui/icons-material";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import IconButton from "@mui/material/IconButton";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableContainer from "@mui/material/TableContainer";
import LinearProgress from '@mui/material/LinearProgress';
import TablePagination from "@mui/material/TablePagination";
import { SIZES, SORT_ORDERS, ALIGN, HTML_TAGS } from "../../../common/constants/commonConstants.js";
import { ASSIGNMENT_TABLE_COLUMN_KEYS, ASSIGNMENT_MESSAGES } from "../../../common/constants/ivrModule/assignmentConstants.js";

const AssignmentTable = ({
    page,
    order,
    setPage,
    orderBy,
    rowsPerPage,
    selectedIds,
    statusFilter,
    statusOptions,
    setRowsPerPage,
    columns,
    assignments,
    onSelectionChange,
    handleRequestSort,
    onStatusFilterChange,
    isAssignmentLoading,
    filteredAssignmentLength
}) => {

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const isSelected = (id) => selectedIds.includes(id);
    const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const allIds = assignments.map((assignment) => assignment.id);
            onSelectionChange([...new Set([...selectedIds, ...allIds])]);
        } else {
            const pageIds = assignments.map((assignment) => assignment.id);
            onSelectionChange(selectedIds.filter((id) => !pageIds.includes(id)));
        }
    };
    const handleClick = (id) => {
        if (isSelected(id)) {
            onSelectionChange(selectedIds.filter((sid) => sid !== id));
        } else {
            onSelectionChange([...selectedIds, id]);
        }
    };
    const handleFilterSelect = (status) => {
        onStatusFilterChange(status);
        handleMenuClose();
    };

    return (
        <>
            <TableContainer sx={{ mt: 2 }}>
                <Table size={SIZES.SMALL}>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    sortDirection={orderBy === column.id ? order : false}
                                    align={column.id === ASSIGNMENT_TABLE_COLUMN_KEYS.SELECT ? ALIGN.CENTER : ALIGN.LEFT}
                                >
                                    {column.id === ASSIGNMENT_TABLE_COLUMN_KEYS.SELECT ? (
                                        <Checkbox
                                            indeterminate={
                                                assignments.some((assignment) => selectedIds.includes(assignment.id)) &&
                                                !assignments.every((assignment) => selectedIds.includes(assignment.id))
                                            }
                                            checked={
                                                assignments.length > 0 &&
                                                assignments.every((assignment) => selectedIds.includes(assignment.id))
                                            }
                                            onChange={handleSelectAllClick}
                                        />
                                    ) : column.id === ASSIGNMENT_TABLE_COLUMN_KEYS.STATUS ? (
                                        <div style={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <TableSortLabel
                                                active={orderBy === column.id}
                                                direction={orderBy === column.id ? order : SORT_ORDERS.ASCENDING}
                                                onClick={() => handleRequestSort(column.id)}
                                            >
                                                {column.label}
                                            </TableSortLabel>
                                            <IconButton size="small" onClick={handleMenuOpen}>
                                                <FilterAltIcon fontSize="small" sx={{ color: statusFilter ? "blue" : "grey" }}  />
                                            </IconButton>
                                            <Menu
                                                anchorEl={anchorEl}
                                                open={open}
                                                onClose={handleMenuClose}
                                            >
                                                <MenuItem
                                                    selected={!statusFilter}
                                                    onClick={() => handleFilterSelect("")}
                                                >
                                                    All
                                                </MenuItem>
                                                {statusOptions.map((status) => (
                                                    <MenuItem
                                                        key={status}
                                                        selected={statusFilter === status}
                                                        onClick={() => handleFilterSelect(status)}
                                                    >
                                                        {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
                                                    </MenuItem>
                                                ))}
                                            </Menu>
                                        </div>
                                    ) : column.isSortable ? (
                                        <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={orderBy === column.id ? order : SORT_ORDERS.ASCENDING}
                                            onClick={() => handleRequestSort(column.id)}
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    ) : column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isAssignmentLoading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align={ALIGN.CENTER} >
                                    <LinearProgress />
                                </TableCell>
                            </TableRow>
                        ) : assignments.length > 0 ? assignments.map((assignment) => {
                        const isItemSelected = isSelected(assignment.id);
                        return (
                            <TableRow
                                hover
                                role="checkbox"
                                tabIndex={-1}
                                key={assignment.id}
                                selected={isItemSelected}
                            >
                                <TableCell align={ALIGN.CENTER}>
                                    <Checkbox
                                        checked={isItemSelected}
                                        onChange={() => handleClick(assignment.id)}
                                    />
                                </TableCell>
                                <TableCell>
                                    {assignment.id}
                                </TableCell>
                                <TableCell>
                                    {assignment.status.charAt(0).toUpperCase() + assignment.status.slice(1).toLowerCase()}
                                </TableCell> 
                                <TableCell>
                                    {assignment.workerId}
                                </TableCell>
                                <TableCell>
                                    <td>{new Date(assignment.assignedAtDate).toLocaleDateString('en-US')}, </td>
                                    <td>{new Date(assignment.assignedAtDate).toLocaleTimeString('en-US', {
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true
                                    })}</td>
                                </TableCell>
                                <TableCell>
                                    {assignment.jobid}
                                </TableCell>
                                  
                                <TableCell>
                                    {assignment.queue}
                                </TableCell>
                            </TableRow>
                        );
                    }) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} align={ALIGN.CENTER}>
                                    {ASSIGNMENT_MESSAGES.NO_ASSIGNMENT}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    component={HTML_TAGS.DIV}
                    count={filteredAssignmentLength}
                    rowsPerPageOptions={[10, 25, 50]}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                        setPage(0);
                        setRowsPerPage(parseInt(event.target.value, 10));
                    }}
                />
            </TableContainer>
        </>
    )
};

export default AssignmentTable;